import assert from "node:assert";
import { existsSync, readFileSync } from "node:fs";
import * as path from "node:path";
import * as vite from "vite";
import { MAIN_ENTRY_NAME } from "../cloudflare-environment";
import { assertIsNotPreview } from "../context";
import { writeDeployConfig } from "../deploy-config";
import { getLocalDevVarsForPreview } from "../dev-vars";
import { createPlugin } from "../utils";
import type { Unstable_RawConfig } from "wrangler";

/**
 * Plugin to generate additional output files as part of the build, including the output `wrangler.json` file.
 */
export const outputConfigPlugin = createPlugin("output-config", (ctx) => {
	return {
		generateBundle(_, bundle) {
			assertIsNotPreview(ctx);

			// Child environments should not emit wrangler.json or .dev.vars files
			if (ctx.isChildEnvironment(this.environment.name)) {
				return;
			}

			let outputConfig: Unstable_RawConfig | undefined;

			const inputWorkerConfig = ctx.getWorkerConfig(this.environment.name);

			if (inputWorkerConfig) {
				const entryChunk = Object.values(bundle).find(
					(chunk) =>
						chunk.type === "chunk" &&
						chunk.isEntry &&
						chunk.name === MAIN_ENTRY_NAME
				);

				assert(
					entryChunk,
					`Expected entry chunk with name "${MAIN_ENTRY_NAME}"`
				);

				const isPrerenderWorker =
					this.environment.name ===
					ctx.resolvedPluginConfig.prerenderWorkerEnvironmentName;
				const isEntryWorker =
					ctx.resolvedPluginConfig.type === "workers" &&
					this.environment.name ===
						ctx.resolvedPluginConfig.entryWorkerEnvironmentName;

				outputConfig = {
					...inputWorkerConfig,
					main: entryChunk.fileName,
					no_bundle: true,
					rules: [{ type: "ESModule", globs: ["**/*.js", "**/*.mjs"] }],
					assets:
						isEntryWorker || isPrerenderWorker
							? {
									...inputWorkerConfig.assets,
									directory: getAssetsDirectory(
										this.environment.config.build.outDir,
										ctx.resolvedViteConfig
									),
								}
							: undefined,
				};

				// Infer `upload_source_maps` from Vite's `build.sourcemap` if not explicitly set
				if (
					inputWorkerConfig.upload_source_maps === undefined &&
					this.environment.config.build.sourcemap
				) {
					outputConfig.upload_source_maps = true;
				}

				if (inputWorkerConfig.configPath) {
					const localDevVars = getLocalDevVarsForPreview(
						inputWorkerConfig,
						ctx.resolvedPluginConfig.cloudflareEnv
					);
					// Save a .dev.vars file to the worker's build output directory if there are local dev vars, so that it will be then detected by `vite preview`.
					if (localDevVars) {
						this.emitFile({
							type: "asset",
							fileName: ".dev.vars",
							source: localDevVars,
						});
					}
				}
			} else if (this.environment.name === "client") {
				const filesToAssetsIgnore = ["wrangler.json", ".dev.vars"] as const;
				const existingAssetsIgnoreContent =
					ctx.resolvedViteConfig.publicDir.length > 0
						? readAssetsIgnoreFile(
								path.join(ctx.resolvedViteConfig.publicDir, ".assetsignore")
							)
						: "";

				this.emitFile({
					type: "asset",
					fileName: ".assetsignore",
					source: `${existingAssetsIgnoreContent}${filesToAssetsIgnore.join("\n")}\n`,
				});

				// For assets only projects the `wrangler.json` file is emitted in the client output directory
				if (ctx.resolvedPluginConfig.type === "assets-only") {
					const inputAssetsOnlyConfig = ctx.resolvedPluginConfig.config;

					outputConfig = {
						...inputAssetsOnlyConfig,
						assets: {
							...inputAssetsOnlyConfig.assets,
							directory: ".",
						},
					};
				}
			}

			if (!outputConfig) {
				return;
			}

			// Set to `undefined` if it's an empty object so that the user doesn't see a warning about using `unsafe` fields when deploying their Worker.
			if (
				outputConfig.unsafe &&
				Object.keys(outputConfig.unsafe).length === 0
			) {
				outputConfig.unsafe = undefined;
			}

			this.emitFile({
				type: "asset",
				fileName: "wrangler.json",
				source: JSON.stringify(outputConfig),
			});
		},
		writeBundle() {
			assertIsNotPreview(ctx);

			// These conditions ensure the deploy config is emitted once per application build as `writeBundle` is called for each environment.
			// If Vite introduces an additional hook that runs after the application has built then we could use that instead.
			if (
				this.environment.name ===
				(ctx.resolvedPluginConfig.type === "workers"
					? ctx.resolvedPluginConfig.entryWorkerEnvironmentName
					: "client")
			) {
				writeDeployConfig(ctx.resolvedPluginConfig, ctx.resolvedViteConfig);
			}
		},
	};
});

function readAssetsIgnoreFile(assetsIgnorePath: string): string {
	const content = existsSync(assetsIgnorePath)
		? readFileSync(assetsIgnorePath, "utf-8")
		: "";
	if (content.length === 0) {
		return "";
	}
	return content.at(-1) === "\n" ? content : `${content}\n`;
}

function getAssetsDirectory(
	workerOutputDirectory: string,
	resolvedViteConfig: vite.ResolvedConfig
): string {
	const clientOutputDirectory =
		resolvedViteConfig.environments.client?.build.outDir;

	assert(
		clientOutputDirectory,
		"Unexpected error: client output directory is undefined"
	);

	return vite.normalizePath(
		path.relative(
			path.resolve(resolvedViteConfig.root, workerOutputDirectory),
			path.resolve(resolvedViteConfig.root, clientOutputDirectory)
		)
	);
}
